import {
  dispatchMapTutorialHelpWindowRequest,
} from "./map-ui-request-contract.js";
import {
  browserTutorialProgressRepository,
} from "../js/account/tutorial-progress-sync-repository.js";
import { browserPresetWorkspace } from "../js/presets/browser-workspace.js";
import { mapText } from "./map-i18n.js";
import { buildLayerPanelStateBundle } from "./map-layer-panel-state.js";
import { resolveLayerEntries } from "./map-layer-state.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import {
  MAP_TUTORIALS,
  hoverHasRegionFacts,
  mapTutorialProgressId,
  normalizedTutorialStep,
  reduceTutorialInteraction,
  selectionTargetsFishingArea,
  selectionTargetsHotspot,
  tutorialStepAction,
} from "./map-tutorial-contract.js";
import {
  chooseTutorialCoachPosition,
  tutorialActionTarget,
  tutorialGuidanceScrollTop,
  tutorialObstacleBounds,
  tutorialTaskStartScrollTop,
  tutorialTargetScrollState,
  tutorialVignetteHoleRect,
} from "./map-tutorial-presentation.js";
import { renderMapTutorialStage } from "./map-tutorial-view.js";

const TUTORIAL_STAGE_TAG_NAME = "fishymap-tutorial-stage";
const FISHYMAP_V2_EVENT = "fishymap:v2";
const HTMLElementBase = globalThis.HTMLElement ?? class {};
const SPOTLIGHT_CLASSES = [
  "outline",
  "outline-4",
  "outline-primary",
  "outline-offset-2",
];
const FOLLOWER_CLASSES = [
  "pointer-events-auto",
  "absolute",
  "left-3",
  "top-3",
  "z-40",
  "block",
  "w-80",
  "max-w-[calc(100%-1.5rem)]",
  "m-0",
  "overflow-visible",
  "border-0",
  "bg-transparent",
  "p-0",
  "not-prose",
];
const TUTORIAL_SESSION_PROPERTY = "__fishymapTutorialSession";
const MAP_PRESET_COLLECTION_KEY = "map-presets";

function tutorialProjectionState(projection) {
  if (projection == null) {
    return null;
  }
  return {
    _map_ui: projection.ui,
    _map_bridged: projection.bridged,
    _map_actions: projection.actions,
    _map_bookmarks: projection.bookmarks,
    _map_character_catalog: projection.characterCatalog,
    _map_character_presence: projection.characterPresence,
    _map_character_stream: projection.characterStream,
    _map_character_activity: projection.characterActivity,
    _map_character_activity_ui: projection.characterActivityUi,
    _map_character_activity_view: projection.characterActivityView,
    _map_session: projection.session,
    _map_runtime: projection.runtime,
    _shared_fish: projection.sharedFish,
  };
}

function projectionAffectsTutorial(previous, next) {
  return !previous ||
    previous.ui !== next.ui ||
    previous.bridged !== next.bridged ||
    previous.bookmarks !== next.bookmarks ||
    previous.runtime !== next.runtime;
}

function mapPresetRows(state) {
  if (!Array.isArray(state?.presets)) {
    return null;
  }
  return new Map(state.presets.map((preset) => [
    String(preset.id),
    {
      name: String(preset.name),
      revision: String(preset.revision),
    },
  ]));
}

function tutorialCompletionProjection(state, progressId) {
  if (state?.kind === "ready" && Array.isArray(state.completedIds)) {
    return {
      kind: "ready",
      status: state.completedIds.includes(progressId)
        ? "completed"
        : "incomplete",
    };
  }
  if (state?.kind === "missing") {
    return { kind: "missing", status: "incomplete" };
  }
  return {
    kind: typeof state?.kind === "string" ? state.kind : "uninitialized",
    status: "unknown",
  };
}

function tutorialProgressCommandOptions(state) {
  if (state?.kind === "ready") {
    return { expectedRevision: state.revision };
  }
  if (state?.kind === "missing") {
    return { expectedRevision: null };
  }
  if (state?.kind === "conflict") {
    return { expectedRevision: state.actualRevision };
  }
  return {};
}

function sameProgress(first, second) {
  return JSON.stringify(first) === JSON.stringify(second);
}

function geometryNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : 0;
}

function rectangleGeometryKey(rectangle, { sizeOnly = false } = {}) {
  if (!rectangle) {
    return "missing";
  }
  const values = sizeOnly
    ? [rectangle.width, rectangle.height]
    : [rectangle.left, rectangle.top, rectangle.width, rectangle.height];
  return values.map(geometryNumber).join(":");
}

function visibleLayerIds(signals) {
  return new Set(
    Array.isArray(signals?._map_bridged?.filters?.layerIdsVisible)
      ? signals._map_bridged.filters.layerIdsVisible.map(String)
      : [],
  );
}

function queryIsActive(signals) {
  const children = signals?._map_ui?.search?.expression?.children;
  return Array.isArray(children) && children.length > 0;
}

function cachedHotspotVisibility() {
  const value = globalThis.window?.FishyMapBridge?.getCurrentState?.()
    ?.hotspotVisibility;
  const totalCount = Number(value?.totalCount);
  const visibleCount = Number(value?.visibleCount);
  if (
    !Number.isSafeInteger(totalCount) ||
    totalCount < 0 ||
    !Number.isSafeInteger(visibleCount) ||
    visibleCount < 0 ||
    visibleCount > totalCount
  ) {
    return null;
  }
  return { totalCount, visibleCount };
}

function expandedLayerIds(signals) {
  return new Set(
    Array.isArray(signals?._map_ui?.layers?.expandedLayerIds)
      ? signals._map_ui.layers.expandedLayerIds.map(String)
      : [],
  );
}

function hoverFactVisible(signals, layerId, factKey) {
  const facts = signals?._map_ui?.layers?.hoverFactsVisibleByLayer?.[layerId];
  return facts?.[factKey] === true;
}

function selectedBookmarkCount(signals) {
  return Array.isArray(signals?._map_ui?.bookmarks?.selectedIds)
    ? signals._map_ui.bookmarks.selectedIds.length
    : 0;
}

function storedBookmarkCount(signals) {
  return Array.isArray(signals?._map_bookmarks?.entries)
    ? signals._map_bookmarks.entries.length
    : 0;
}

function storedBookmarkIds(signals) {
  return Array.isArray(signals?._map_bookmarks?.entries)
    ? signals._map_bookmarks.entries
        .map((bookmark) => String(bookmark?.id || "").trim())
        .filter(Boolean)
    : [];
}

function resolvedLayer(signals, layerId) {
  const normalizedLayerId = String(layerId ?? "").trim();
  return resolveLayerEntries(
    buildLayerPanelStateBundle(signals),
  ).find(
    (candidate) => candidate.layerId === normalizedLayerId,
  );
}

function layerOpacity(signals, layerId) {
  const layer = resolvedLayer(signals, layerId);
  return Number.isFinite(layer?.opacity) ? layer.opacity : null;
}

function layerClipMask(signals, layerId) {
  return String(resolvedLayer(signals, layerId)?.clipMaskLayerId || "").trim();
}

function windowState(signals, windowId) {
  const state = signals?._map_ui?.windowUi?.[windowId] || {};
  return {
    windowId,
    open: state.open !== false,
    collapsed: state.collapsed === true,
    x: state.x,
    y: state.y,
  };
}

export class FishyMapTutorialStageElement extends HTMLElementBase {
  static get observedAttributes() {
    return ["data-lesson", "data-step"];
  }

  constructor() {
    super();
    this._shell = null;
    this._mapShellProjection = null;
    this._spotlight = null;
    this._vignette = null;
    this._promotedSurface = null;
    this._promotedSurfaceZIndex = "";
    this._frameId = 0;
    this._presentationInvalidated = false;
    this._presentationGeometryKey = "";
    this._presentationTarget = null;
    this._renderQueued = false;
    this._renderedHistoryKey = "";
    this._enteredStageKey = "";
    this._progress = {};
    this._lessonContext = {};
    this._completed = false;
    this._completionFailure = null;
    this._completionOperation = null;
    this._advancing = false;
    this._paused = false;
    this._popoverOpen = false;
    this._homeParent = null;
    this._homeNextSibling = null;
    this._relocating = false;
    this._pendingLayerSettingsId = "";
    this._pendingLayerVisibilityId = "";
    this._pendingLayerHoverFact = null;
    this._infoPanelObserver = null;
    this._tutorialProgressRepository = browserTutorialProgressRepository();
    this._tutorialProgressState = this._tutorialProgressRepository.state();
    this._unsubscribeTutorialProgress = null;
    this._presetWorkspace = browserPresetWorkspace();
    this._mapPresetRows = null;
    this._unsubscribePresetWorkspace = null;
    this._followPresentationFrame = () => this.followPresentationFrame();
    this._handleClick = (event) => {
      if (event.target.closest?.("[data-tutorial-completion-retry]")) {
        event.preventDefault?.();
        return this.completeStep();
      }
      if (event.target.closest?.("[data-tutorial-proceed]")) {
        event.preventDefault?.();
        return this.observe({ kind: "tutorial-proceeded" });
      }
      if (event.target.closest?.("[data-tutorial-hide]")) {
        event.preventDefault?.();
        this.pauseTutorial();
        return;
      }
      if (!event.target.closest?.("[data-tutorial-exit]")) {
        return;
      }
      event.preventDefault?.();
      this.reset();
    };
    this._handleShellClick = (event) => {
      if (event.target === this || this.contains?.(event.target)) {
        return;
      }
      const cancel = event.target?.closest?.("[data-tutorial-cancel]");
      if (cancel) {
        this.reset();
        return;
      }
      const start = event.target?.closest?.("[data-tutorial-start]");
      if (start) {
        this.startTutorial(start.getAttribute?.("data-tutorial-start"));
        return;
      }
      this.observeClick(event);
      this.schedulePresentation();
    };
    this._handleShellPointerDown = (event) => {
      if (event.target === this || this.contains?.(event.target)) {
        return;
      }
      this.schedulePresentation();
    };
    this._handleShellInput = (event) => {
      if (event.target?.matches?.("#fishymap-search")) {
        this.observeQueryState(event.target.value);
      }
    };
    this._handleV2EventBatch = (event) => {
      const events = Array.isArray(event?.detail?.events) ? event.detail.events : [];
      for (const entry of events) {
        if (entry?.type === "selection-changed") {
          this.observeSelection(entry);
        } else if (entry?.type === "hotspot-visibility-changed") {
          this.observeHotspotVisibility(entry);
        } else if (entry?.type === "hover-changed") {
          this.observeRegionHover(entry);
        }
      }
    };
    this._handleShellScroll = (event) => {
      if (event.target === this || this.contains?.(event.target)) {
        return;
      }
      this.schedulePresentation();
    };
    this._handlePresetWorkspace = (state) => {
      const nextRows = mapPresetRows(state);
      if (!nextRows) {
        return;
      }
      const previousRows = this._mapPresetRows;
      this._mapPresetRows = nextRows;
      if (!previousRows) {
        return;
      }
      for (const [presetId, preset] of nextRows) {
        const previous = previousRows.get(presetId);
        const reason = !previous
          ? "create-preset"
          : previous.revision !== preset.revision || previous.name !== preset.name
            ? "update-preset"
            : "";
        if (reason) {
          this.observe({
            kind: "preset-changed",
            reason,
            collectionKey: MAP_PRESET_COLLECTION_KEY,
          });
        }
      }
    };
    this._handleLanguageChange = () => {
      this.updateCatalogControls();
      this.queueRender();
    };
    this._handleTutorialProgress = (state) => {
      this._tutorialProgressState = state;
      this.updateCatalogControls();
    };
    this._handleViewportChange = () => this.schedulePresentation();
  }

  get mapShellProjection() {
    return this._mapShellProjection;
  }

  set mapShellProjection(projection) {
    const next = assertMapShellProjectionV1(projection);
    const previous = this._mapShellProjection;
    if (previous && next.revision < previous.revision) {
      throw new RangeError("Map tutorial projection revision may not regress");
    }
    if (previous && next.revision === previous.revision) {
      return;
    }
    const affectsTutorial = projectionAffectsTutorial(previous, next);
    this._mapShellProjection = next;
    if (previous && affectsTutorial) {
      this.observeProjectionChange(previous, next);
    }
    if (this.isConnected && affectsTutorial) {
      this.queueRender();
      this.schedulePresentation();
    }
  }

  projectionState() {
    return tutorialProjectionState(this._mapShellProjection);
  }

  get tutorialProgressRepository() {
    return this._tutorialProgressRepository;
  }

  set tutorialProgressRepository(repository) {
    if (
      !repository ||
      typeof repository.state !== "function" ||
      typeof repository.complete !== "function" ||
      typeof repository.subscribe !== "function"
    ) {
      throw new TypeError(
        "tutorialProgressRepository must implement the tutorial progress contract",
      );
    }
    if (repository === this._tutorialProgressRepository) {
      return;
    }
    this.invalidateCompletionOperation();
    this.unsubscribeTutorialProgress();
    this._tutorialProgressRepository = repository;
    this._tutorialProgressState = repository.state();
    if (this.isConnected) {
      this.subscribeTutorialProgress();
    }
    this.updateCatalogControls();
  }

  subscribeTutorialProgress() {
    if (this._unsubscribeTutorialProgress) {
      return;
    }
    this._unsubscribeTutorialProgress = this._tutorialProgressRepository.subscribe(
      this._handleTutorialProgress,
    );
  }

  unsubscribeTutorialProgress() {
    this._unsubscribeTutorialProgress?.();
    this._unsubscribeTutorialProgress = null;
  }

  get presetWorkspace() {
    return this._presetWorkspace;
  }

  set presetWorkspace(workspace) {
    if (
      !workspace
      || typeof workspace.state !== "function"
      || typeof workspace.subscribe !== "function"
    ) {
      throw new TypeError(
        "presetWorkspace must implement the preset workspace observation contract",
      );
    }
    if (workspace === this._presetWorkspace) {
      return;
    }
    this.unsubscribePresetWorkspace();
    this._presetWorkspace = workspace;
    if (this.isConnected) {
      this.subscribePresetWorkspace();
    }
  }

  subscribePresetWorkspace() {
    if (this._unsubscribePresetWorkspace) {
      return;
    }
    this._mapPresetRows = null;
    this._unsubscribePresetWorkspace = this._presetWorkspace.subscribe(
      MAP_PRESET_COLLECTION_KEY,
      this._handlePresetWorkspace,
    );
  }

  unsubscribePresetWorkspace() {
    this._unsubscribePresetWorkspace?.();
    this._unsubscribePresetWorkspace = null;
    this._mapPresetRows = null;
  }

  connectedCallback() {
    if (this._relocating) {
      this._relocating = false;
      return;
    }
    if (!this._homeParent) {
      this._homeParent = this.parentNode || null;
      this._homeNextSibling = this.nextSibling || null;
    }
    this._shell = this.closest?.("#map-page-shell") || null;
    this.addEventListener?.("click", this._handleClick);
    this._shell?.addEventListener?.("click", this._handleShellClick, true);
    this._shell?.addEventListener?.(
      "pointerdown",
      this._handleShellPointerDown,
      true,
    );
    this._shell?.addEventListener?.("input", this._handleShellInput);
    this._shell?.addEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    this._shell?.addEventListener?.("scroll", this._handleShellScroll, true);
    const infoPanel = this._shell?.querySelector?.("#fishymap-info-panel") || null;
    if (infoPanel && typeof globalThis.MutationObserver === "function") {
      this._infoPanelObserver = new globalThis.MutationObserver(() => {
        this.observeCatchProfileState();
        this.schedulePresentation();
      });
      this._infoPanelObserver.observe(infoPanel, {
        attributes: true,
        attributeFilter: [
          "data-active-pane-id",
          "data-catch-profile-state",
          "data-selection-generation",
        ],
      });
    }
    globalThis.window?.addEventListener?.(
      globalThis.window?.__fishystuffLanguage?.event || "fishystuff:languagechange",
      this._handleLanguageChange,
    );
    this.subscribePresetWorkspace();
    this.subscribeTutorialProgress();
    globalThis.window?.addEventListener?.("resize", this._handleViewportChange, {
      passive: true,
    });
    this.updateCatalogControls();
    this.queueRender();
  }

  disconnectedCallback() {
    if (this._relocating) {
      return;
    }
    this.invalidateCompletionOperation();
    this.removeEventListener?.("click", this._handleClick);
    this._shell?.removeEventListener?.("click", this._handleShellClick, true);
    this._shell?.removeEventListener?.(
      "pointerdown",
      this._handleShellPointerDown,
      true,
    );
    this._shell?.removeEventListener?.("input", this._handleShellInput);
    this._shell?.removeEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    this._shell?.removeEventListener?.("scroll", this._handleShellScroll, true);
    this._infoPanelObserver?.disconnect?.();
    this._infoPanelObserver = null;
    globalThis.window?.removeEventListener?.(
      globalThis.window?.__fishystuffLanguage?.event || "fishystuff:languagechange",
      this._handleLanguageChange,
    );
    this.unsubscribePresetWorkspace();
    this.unsubscribeTutorialProgress();
    globalThis.window?.removeEventListener?.("resize", this._handleViewportChange);
    this.stopPresentationFollowing();
    this.clearSpotlight();
    this.removeVignette();
    this.restorePromotedSurface();
    this.hideFollowerPopover();
    this._renderQueued = false;
    this._renderedHistoryKey = "";
    this._enteredStageKey = "";
    this._shell = null;
  }

  attributeChangedCallback(name, previousValue, nextValue) {
    if (
      previousValue !== nextValue &&
      (name === "data-lesson" || name === "data-step")
    ) {
      this.invalidateCompletionOperation();
    }
    if (previousValue !== nextValue && this.isConnected) {
      this.queueRender();
    }
  }

  queueRender() {
    if (this._renderQueued) {
      return;
    }
    this._renderQueued = true;
    globalThis.queueMicrotask(() => {
      this._renderQueued = false;
      if (this.isConnected) {
        this.render();
      }
    });
  }

  render() {
    this.classList?.add?.(...FOLLOWER_CLASSES);
    const lessonId = this.getAttribute?.("data-lesson") || "";
    const rawStep = this.getAttribute?.("data-step");
    const tutorial = MAP_TUTORIALS[String(lessonId).trim()];
    if (this._paused && tutorial) {
      this.stopPresentationFollowing();
      this.hidden = true;
      this.innerHTML = "";
      this.updateCatalogControls();
      return;
    }
    if (!tutorial) {
      this.stopPresentationFollowing();
      this._enteredStageKey = "";
      this._completed = false;
      this._advancing = false;
      this.clearSpotlight();
      this.hideFollowerPopover();
      this.exitFollowerMode();
      this.innerHTML = lessonId
        ? renderMapTutorialStage(lessonId, rawStep)
        : "";
      this.hidden = !lessonId;
      return;
    }

    this.enterFollowerMode();
    this.hidden = false;
    this.showFollowerPopover();
    const stepIndex = normalizedTutorialStep(rawStep, tutorial.steps.length);
    const stageKey = `${lessonId}:${stepIndex}`;
    let enteredStage = false;
    if (stageKey !== this._enteredStageKey) {
      const signals = this.projectionState();
      const action = tutorial.steps[stepIndex]?.action || "";
      this._enteredStageKey = stageKey;
      this._completed = false;
      this._advancing = false;
      this._pendingLayerSettingsId = "";
      this._pendingLayerVisibilityId = "";
      this._pendingLayerHoverFact = null;
      const visible = visibleLayerIds(signals);
      const searchWindow = windowState(signals, "search");
      const layersWindow = windowState(signals, "layers");
      const hotspotVisibility = cachedHotspotVisibility();
      const hotspotsLayerVisible = visible.has("hotspots");
      const activeQuery = queryIsActive(signals);
      this._progress = {
        bookmarkCount: storedBookmarkCount(signals),
        bookmarkIds: storedBookmarkIds(signals),
        bookmarkSelected: selectedBookmarkCount(signals) > 0,
        ...(action === "activate-bookmark"
          ? {
              createdBookmarkId: String(
                this._lessonContext.createdBookmarkId || "",
              ).trim(),
            }
          : {}),
        ...(action === "cycle-query-window"
          ? {
              windowCyclePhase: searchWindow.open
                ? "needs-close"
                : "needs-initial-open",
            }
          : {}),
        ...(action === "cycle-layer-collapse"
          ? {
              collapseCyclePhase: layersWindow.collapsed
                ? "needs-initial-expand"
                : "needs-collapse",
            }
          : {}),
        ...(action === "move-layer-window"
          ? {
              initialWindowX: layersWindow.x,
              initialWindowY: layersWindow.y,
            }
          : {}),
        ...(action === "show-hotspots-layer"
          ? {
              hotspotsLayerVisible,
              hotspotsHidden: !hotspotsLayerVisible,
              queryActive: activeQuery,
              hotspotTotalCount: hotspotVisibility?.totalCount || 0,
              hotspotVisibleCount: hotspotVisibility?.visibleCount || 0,
              hotspotsFilteredByQuery:
                hotspotsLayerVisible &&
                activeQuery &&
                (hotspotVisibility?.totalCount || 0) > 0 &&
                (hotspotVisibility?.visibleCount || 0) === 0,
            }
          : {}),
        ...(action === "cycle-fish-evidence-settings"
          ? {
              fishEvidenceSettingsInitiallyExpanded:
                expandedLayerIds(signals).has("fish_evidence"),
              fishEvidenceSettingsClosed:
                !expandedLayerIds(signals).has("fish_evidence"),
            }
          : {}),
        ...(action === "change-fish-evidence-opacity"
          ? {
              initialLayerOpacity: layerOpacity(signals, "fish_evidence"),
            }
          : {}),
        ...([
          "enable-origin-hover-fact",
          "enable-resources-hover-fact",
        ].includes(action)
          ? {
              hoverFactInitiallyEnabled: action === "enable-origin-hover-fact"
                ? hoverFactVisible(signals, "regions", "origin_region")
                : hoverFactVisible(
                    signals,
                    "region_groups",
                    "resource_group",
                  ),
              hoverFactDisabled: false,
            }
          : {}),
        ...(action === "detach-regions-clip"
          ? {
              regionsClipInitiallyDetached:
                !layerClipMask(signals, "regions"),
              regionsClipAttached:
                layerClipMask(signals, "regions") === "zone_mask",
            }
          : {}),
      };
      enteredStage = true;
    }
    if (enteredStage && this.reconcileCurrentStep()) {
      return;
    }
    const previousTaskList = this.querySelector?.("[data-tutorial-task-list]");
    const previousGuidanceKey = String(
      this.querySelector?.("[data-tutorial-live-guidance]")?.getAttribute?.(
        "data-tutorial-live-guidance",
      ) || "",
    );
    const previousScrollTop = Number(previousTaskList?.scrollTop) || 0;
    const expandedSteps = new Set(
      Array.from(
        this.querySelectorAll?.("details[data-tutorial-task][open]") || [],
        (task) => Number(task.getAttribute?.("data-tutorial-step")),
      ).filter(Number.isInteger),
    );
    const historyKey = `${lessonId}:${stepIndex}:${this._completed ? "complete" : "active"}`;
    const historyChanged = historyKey !== this._renderedHistoryKey;
    if (historyChanged) {
      expandedSteps.clear();
      if (this._completed) {
        expandedSteps.add(stepIndex);
      } else if (stepIndex > 0) {
        expandedSteps.add(stepIndex - 1);
      }
    }
    this.innerHTML = `${this.completionFailureMarkup()}${renderMapTutorialStage(
      lessonId,
      stepIndex,
      {
        completed: this._completed,
        expandedSteps,
        progress: this._progress,
      },
    )}`;
    const taskList = this.querySelector?.("[data-tutorial-task-list]");
    if (taskList) {
      if (historyChanged) {
        const currentTask = this.querySelector?.(
          `[data-tutorial-task][data-tutorial-step="${stepIndex}"]`,
        );
        taskList.scrollTop = stepIndex > 0
          ? tutorialTaskStartScrollTop(taskList, currentTask)
          : 0;
      } else {
        taskList.scrollTop = previousScrollTop;
        const guidance = this.querySelector?.(
          "[data-tutorial-live-guidance]",
        );
        const guidanceKey = String(
          guidance?.getAttribute?.("data-tutorial-live-guidance") || "",
        );
        if (guidanceKey && guidanceKey !== previousGuidanceKey) {
          taskList.scrollTop = tutorialGuidanceScrollTop(taskList, guidance);
        }
      }
    }
    this._renderedHistoryKey = historyKey;
    this.updateCatalogControls();
    if (this._completed) {
      this.clearSpotlight();
      return;
    }
    this.schedulePresentation();
  }

  enterFollowerMode() {
    if (!this._shell || this._shell[TUTORIAL_SESSION_PROPERTY]) {
      return;
    }
    const helpState = this.projectionState()?._map_ui?.windowUi?.help;
    this._shell[TUTORIAL_SESSION_PROPERTY] = {
      help: {
        open: helpState?.open !== false,
        collapsed: helpState?.collapsed === true,
      },
    };
    this.requestHelpWindowTransition({ transition: "suspend" });
  }

  requestHelpWindowTransition(request) {
    return dispatchMapTutorialHelpWindowRequest(this, request);
  }

  showFollowerPopover() {
    this.setAttribute?.("popover", "manual");
    let alreadyOpen = this._popoverOpen;
    if (typeof this.matches === "function") {
      try {
        alreadyOpen = this.matches(":popover-open");
      } catch (_error) {
        // Older engines do not expose the popover-open selector.
      }
    }
    if (alreadyOpen || typeof this.showPopover !== "function") {
      this._popoverOpen = alreadyOpen;
      return;
    }
    try {
      this.showPopover();
      this._popoverOpen = true;
    } catch (_error) {
      this._popoverOpen = false;
    }
  }

  hideFollowerPopover() {
    let currentlyOpen = this._popoverOpen;
    if (typeof this.matches === "function") {
      try {
        currentlyOpen = this.matches(":popover-open");
      } catch (_error) {
        // Older engines do not expose the popover-open selector.
      }
    }
    if (!currentlyOpen || typeof this.hidePopover !== "function") {
      this._popoverOpen = false;
      return;
    }
    try {
      this.hidePopover();
    } catch (_error) {
      // The browser may already have closed it while the element disconnected.
    }
    this._popoverOpen = false;
  }

  hostFollowerInDialog(dialog) {
    if (!dialog || this.parentNode === dialog) {
      this._popoverOpen = false;
      this.showFollowerPopover();
      return;
    }
    this.hideFollowerPopover();
    this._relocating = true;
    dialog.appendChild?.(this);
    this.showFollowerPopover();
  }

  restoreFollowerHome({ show = true } = {}) {
    const home = this._homeParent;
    if (!home || this.parentNode === home) {
      return;
    }
    this.hideFollowerPopover();
    this._relocating = true;
    if (this._homeNextSibling?.parentNode === home) {
      home.insertBefore?.(this, this._homeNextSibling);
    } else {
      home.appendChild?.(this);
    }
    if (show) {
      this.showFollowerPopover();
    }
  }

  exitFollowerMode({ restoreHelp = true } = {}) {
    const session = this._shell?.[TUTORIAL_SESSION_PROPERTY];
    if (!session) {
      return;
    }
    delete this._shell[TUTORIAL_SESSION_PROPERTY];
    if (!restoreHelp) {
      this.requestHelpWindowTransition({
        transition: "close-expanded",
      });
      return;
    }
    this.requestHelpWindowTransition({
      transition: "restore",
      state: {
        open: session.help?.open !== false,
        collapsed: session.help?.collapsed === true,
      },
    });
  }

  activeAction() {
    return tutorialStepAction(
      this.getAttribute?.("data-lesson"),
      this.getAttribute?.("data-step"),
    );
  }

  catalogText(key, fallback) {
    const translated = mapText(key);
    return translated === `map.${key}` ? fallback : translated;
  }

  setCompletionFailure(kind, reason) {
    const normalizedKind = [
      "conflict",
      "invalid",
      "unsupportedVersion",
      "unavailable",
    ].includes(kind)
      ? kind
      : "unavailable";
    this._completionFailure = {
      kind: normalizedKind,
      reason: String(reason || "repository-command-failed"),
    };
    this.setAttribute?.("data-tutorial-completion-state", normalizedKind);
  }

  clearCompletionFailure() {
    this._completionFailure = null;
    this.removeAttribute?.("data-tutorial-completion-state");
  }

  invalidateCompletionOperation() {
    if (!this._completionOperation) {
      return;
    }
    this._completionOperation = null;
    this._advancing = false;
  }

  completionOperationIsCurrent(operation) {
    if (this._completionOperation !== operation) {
      return false;
    }
    const lessonId = String(
      this.getAttribute?.("data-lesson") || "",
    ).trim();
    const tutorial = MAP_TUTORIALS[lessonId];
    const stepIndex = normalizedTutorialStep(
      this.getAttribute?.("data-step"),
      tutorial?.steps?.length || 0,
    );
    return lessonId === operation.lessonId && stepIndex === operation.stepIndex;
  }

  failCompletionOperation(operation, reason) {
    if (!this.completionOperationIsCurrent(operation)) {
      if (this._completionOperation === operation) {
        this.invalidateCompletionOperation();
      }
      return false;
    }
    this._completionOperation = null;
    this._completed = false;
    this._advancing = false;
    this.setCompletionFailure("unavailable", reason);
    this.updateCatalogControls();
    this.queueRender();
    return true;
  }

  settleCompletionOperation(operation, state) {
    if (!this.completionOperationIsCurrent(operation)) {
      if (this._completionOperation === operation) {
        this.invalidateCompletionOperation();
      }
      return false;
    }
    this._completionOperation = null;
    if (state && typeof state.kind === "string") {
      this._tutorialProgressState = state;
    }
    if (
      state?.kind === "ready" &&
      Array.isArray(state.completedIds) &&
      state.completedIds.includes(operation.progressId)
    ) {
      this.clearCompletionFailure();
      this._completed = true;
      this._advancing = false;
      this.stopPresentationFollowing();
      this.updateCatalogControls();
      this.queueRender();
      return true;
    }
    this._completed = false;
    this._advancing = false;
    this.setCompletionFailure(
      state?.kind,
      state?.reason || "repository-command-not-ready",
    );
    this.updateCatalogControls();
    this.queueRender();
    return true;
  }

  completionFailureMarkup() {
    if (!this._completionFailure) {
      return "";
    }
    const conflict = this._completionFailure.kind === "conflict";
    return `
      <div class="alert ${conflict ? "alert-warning" : "alert-error"} mb-2 items-start text-sm" role="alert" data-tutorial-completion-failure="${this._completionFailure.kind}">
        <span>${
          conflict
            ? "Tutorial progress changed before this completion could be saved."
            : "Tutorial completion could not be saved."
        }</span>
        <button class="btn btn-sm" type="button" data-tutorial-completion-retry>Retry save</button>
      </div>
    `;
  }

  updateCatalogControls() {
    const activeLessonId = String(
      this.getAttribute?.("data-lesson") || "",
    ).trim();
    for (const button of this._shell?.querySelectorAll?.(
      "[data-tutorial-start]",
    ) || []) {
      const lessonId = String(
        button.getAttribute?.("data-tutorial-start") || "",
      ).trim();
      const continuing = this._paused && lessonId === activeLessonId;
      const label =
        button.querySelector?.("[data-tutorial-start-label]") || button;
      label.textContent = continuing
        ? this.catalogText("tutorial.continue", "Continue")
        : this.catalogText("tutorial.start", "Start");
      const cancel = button.parentElement?.querySelector?.(
        `[data-tutorial-cancel="${lessonId}"]`,
      );
      if (cancel) {
        cancel.hidden = !continuing;
      }
    }
    for (const completion of this._shell?.querySelectorAll?.(
      "[data-tutorial-complete]",
    ) || []) {
      const lessonId = String(
        completion.getAttribute?.("data-tutorial-complete") || "",
      ).trim();
      const projectionState =
        this._completionFailure && lessonId === activeLessonId
          ? { kind: this._completionFailure.kind }
          : this._tutorialProgressState;
      const projection = tutorialCompletionProjection(
        projectionState,
        mapTutorialProgressId(lessonId),
      );
      const unknown = projection.status === "unknown";
      completion.hidden = projection.status === "incomplete";
      completion.setAttribute?.(
        "data-tutorial-progress-state",
        projection.status,
      );
      completion.setAttribute?.("data-tutorial-progress-kind", projection.kind);
      completion.classList?.toggle?.("text-success", !unknown);
      completion.classList?.toggle?.("text-warning", unknown);
      const icon = completion.querySelector?.("use");
      icon?.setAttribute?.(
        "href",
        unknown ? "#fishy-question-mark" : "#fishy-check-badge-solid",
      );
      const label = completion.querySelector?.(".sr-only");
      if (label) {
        label.textContent = unknown
          ? mapText("layers.unavailable")
          : this.catalogText("tutorial.task_completed", "Completed");
      }
    }
  }

  startTutorial(lessonId) {
    const normalizedLessonId = String(lessonId ?? "").trim();
    if (!MAP_TUTORIALS[normalizedLessonId]) {
      return false;
    }
    this.invalidateCompletionOperation();
    this.clearCompletionFailure();
    const activeLessonId = String(
      this.getAttribute?.("data-lesson") || "",
    ).trim();
    this.clearSpotlight();
    this.restorePromotedSurface();
    this._enteredStageKey = "";
    this._renderedHistoryKey = "";
    if (this._paused && normalizedLessonId === activeLessonId) {
      this._paused = false;
      this._advancing = false;
    } else {
      this._paused = false;
      this._progress = {};
      this._lessonContext = {};
      this._completed = false;
      this._advancing = false;
      this.setAttribute?.("data-lesson", normalizedLessonId);
      this.setAttribute?.("data-step", "0");
    }
    this.updateCatalogControls();
    this.queueRender();
    return true;
  }

  pauseTutorial() {
    if (
      this.activeAction() !== "hide-and-resume-tutorial" ||
      !this._enteredStageKey ||
      this._completed
    ) {
      return false;
    }
    const lessonId = String(
      this.getAttribute?.("data-lesson") || "",
    ).trim();
    const tutorial = MAP_TUTORIALS[lessonId];
    const stepIndex = normalizedTutorialStep(
      this.getAttribute?.("data-step"),
      tutorial?.steps?.length || 0,
    );
    if (!tutorial || stepIndex >= tutorial.steps.length - 1) {
      return false;
    }
    this.clearSpotlight();
    this.restorePromotedSurface();
    this.hideFollowerPopover();
    this.stopPresentationFollowing();
    this._paused = true;
    this._enteredStageKey = "";
    this._renderedHistoryKey = "";
    this._advancing = false;
    this.setAttribute?.("data-step", String(stepIndex + 1));
    this.exitFollowerMode({ restoreHelp: false });
    this.innerHTML = "";
    this.hidden = true;
    const helpButton =
      this._shell?.querySelector?.('[data-window-toggle="help"]') || null;
    this._spotlight = helpButton;
    helpButton?.classList?.add?.(...SPOTLIGHT_CLASSES);
    this.updateCatalogControls();
    return true;
  }

  observeClick(event) {
    if (!this._enteredStageKey || this._completed || this._advancing) {
      return;
    }
    const target = event?.target;
    if (!target?.closest) {
      return;
    }
    if (target.closest("#fishymap-focus-search")) {
      this.observe({ kind: "query-focused" });
      return;
    }
    const detailsBookmarkCopy = target.closest("#fishymap-zone-info-status");
    if (detailsBookmarkCopy && detailsBookmarkCopy.disabled !== true) {
      this.observe({ kind: "details-bookmark-copied" });
      return;
    }
    const provenance = target.closest(
      "#fishymap-info-panel [data-fishy-provenance-channel]",
    );
    if (provenance) {
      const channel = String(
        provenance.getAttribute?.("data-fishy-provenance-channel") || "",
      ).trim();
      const expectedAction = channel === "presence"
        ? "inspect-presence-provenance"
        : channel === "rate"
          ? "inspect-rate-provenance"
          : "";
      const expectedTarget = expectedAction
        ? tutorialActionTarget(
            expectedAction,
            this._shell,
            {},
            this._mapShellProjection,
          )
        : null;
      this.observe({
        kind: "provenance-opened",
        channel,
        firstFish: provenance === expectedTarget,
      });
      return;
    }
    const zoneInfoTab = target.closest("button[data-zone-info-tab]");
    if (zoneInfoTab) {
      this.observe({
        kind: "zone-info-tab-selected",
        paneId: String(
          zoneInfoTab.getAttribute?.("data-zone-info-tab") || "",
        ).trim(),
      });
      return;
    }
    const layerHoverFact = target.closest(
      "input[data-layer-hover-fact-key]",
    );
    if (layerHoverFact) {
      const layerId = String(
        layerHoverFact.getAttribute?.("data-layer-hover-fact-layer-id") || "",
      ).trim();
      const factKey = String(
        layerHoverFact.getAttribute?.("data-layer-hover-fact-key") || "",
      ).trim();
      this._pendingLayerHoverFact = layerId && factKey
        ? { layerId, factKey }
        : null;
      return;
    }
    const layerSettings = target.closest("[data-layer-settings-toggle]");
    if (layerSettings && !target.closest("button[data-layer-visibility]")) {
      this._pendingLayerSettingsId = String(
        layerSettings.getAttribute?.("data-layer-settings-toggle") || "",
      ).trim();
      return;
    }
    const layerVisibility = target.closest("button[data-layer-visibility]");
    if (layerVisibility) {
      this._pendingLayerVisibilityId = String(
        layerVisibility.getAttribute?.("data-layer-visibility") || "",
      ).trim();
      return;
    }
    const bookmarkActivation = target.closest(
      "button[data-bookmark-activate]",
    );
    if (bookmarkActivation) {
      this.observe({
        kind: "bookmark-activated",
        bookmarkId: String(
          bookmarkActivation.getAttribute?.("data-bookmark-activate") || "",
        ).trim(),
      });
      return;
    }
    const bookmarkExport = target.closest(
      "#fishymap-bookmark-copy-selected, #fishymap-bookmark-export",
    );
    if (bookmarkExport && bookmarkExport.disabled !== true) {
      this.observe({ kind: "bookmark-exported" });
      return;
    }
    const bookmarkImport = target.closest(
      "#fishymap-bookmark-import-trigger, #fishymap-bookmark-paste",
    );
    if (bookmarkImport && bookmarkImport.disabled !== true) {
      this.observe({ kind: "bookmark-import-opened" });
      return;
    }
    if (
      target.closest(
        'fishy-preset-manager[data-preset-collection="map-presets"] [data-role="open"]',
      )
    ) {
      this.observe({ kind: "map-presets-opened" });
    }
  }

  observeSelection(selection) {
    const action = this.activeAction();
    if (
      !this._enteredStageKey ||
      this._completed ||
      this._advancing
    ) {
      return;
    }
    if (action === "inspect-hotspot") {
      if (selectionTargetsHotspot(selection)) {
        this.observe({ kind: "hotspot-selected" });
      } else {
        this._progress = {
          ...this._progress,
          selectionRejected: "not-hotspot",
        };
        this.queueRender();
        this.schedulePresentation();
      }
      return;
    }
    if (action !== "select-map-point") {
      return;
    }
    if (!selectionTargetsFishingArea(selection)) {
      this._progress = {
        ...this._progress,
        awaitingCatchProfile: false,
        selectionGeneration: "",
        selectionRejected: "landmark",
      };
      this.queueRender();
      this.schedulePresentation();
      return;
    }
    this._progress = {
      ...this._progress,
      awaitingCatchProfile: true,
      selectionGeneration: String(selection?.detailsGeneration || ""),
      selectionRejected: "",
    };
    this.queueRender();
    this.schedulePresentation();
  }

  observeRegionHover(hover) {
    if (
      !this._enteredStageKey ||
      this._completed ||
      this._advancing ||
      this.activeAction() !== "inspect-region-hover"
    ) {
      return;
    }
    const signals = this.projectionState();
    if (
      hoverFactVisible(signals, "regions", "origin_region") &&
      hoverFactVisible(signals, "region_groups", "resource_group") &&
      hoverHasRegionFacts(hover)
    ) {
      this.observe({ kind: "region-hovered" });
    }
  }

  observeHotspotVisibility(visibility) {
    if (
      !this._enteredStageKey ||
      this._completed ||
      this._advancing ||
      this.activeAction() !== "show-hotspots-layer"
    ) {
      return;
    }
    const totalCount = Number(visibility?.totalCount);
    const visibleCount = Number(visibility?.visibleCount);
    if (
      !Number.isSafeInteger(totalCount) ||
      totalCount < 0 ||
      !Number.isSafeInteger(visibleCount) ||
      visibleCount < 0 ||
      visibleCount > totalCount
    ) {
      return;
    }
    const signals = this.projectionState();
    this.observe({
      kind: "hotspot-visibility-changed",
      totalCount,
      visibleCount,
      layerVisible: visibleLayerIds(signals).has("hotspots"),
      queryActive: queryIsActive(signals),
    });
  }

  observeCatchProfileState() {
    if (
      !this._enteredStageKey ||
      this._completed ||
      this._advancing ||
      this.activeAction() !== "select-map-point" ||
      this._progress.awaitingCatchProfile !== true
    ) {
      return;
    }
    const infoPanel = this._shell?.querySelector?.("#fishymap-info-panel") || null;
    const expectedGeneration = String(this._progress.selectionGeneration || "");
    const renderedGeneration = String(
      infoPanel?.getAttribute?.("data-selection-generation") || "",
    );
    if (expectedGeneration && renderedGeneration !== expectedGeneration) {
      return;
    }
    const state = String(
      infoPanel?.getAttribute?.("data-catch-profile-state") || "",
    );
    if (state === "available") {
      this.observe({ kind: "catch-profile-ready" });
    }
  }

  reconcileCurrentStep() {
    const action = this.activeAction();
    const signals = this.projectionState();
    const visible = visibleLayerIds(signals);
    if (action === "confirm-pink-dolphin") {
      this.observeQueryState(
        this._shell?.querySelector?.("#fishymap-search")?.value || "",
      );
      if (this._advancing || this._completed) {
        return true;
      }
    }
    if (action === "show-hotspots-layer") {
      const visibility = cachedHotspotVisibility();
      if (visibility) {
        this.observeHotspotVisibility(visibility);
        if (this._advancing || this._completed) {
          return true;
        }
      }
    }
    if (action === "hide-hotspots-layer" && !visible.has("hotspots")) {
      this.observe({
        kind: "layer-visibility-changed",
        layerId: "hotspots",
        visible: false,
      });
      return true;
    }
    if (action === "close-fish-evidence-settings" &&
      !expandedLayerIds(signals).has("fish_evidence")) {
      this.observe({
        kind: "layer-settings-changed",
        layerId: "fish_evidence",
        expanded: false,
      });
      return true;
    }
    return false;
  }

  observeQueryState(queryText) {
    const signals = this.projectionState();
    this.observe({
      kind: "query-state-changed",
      expression: signals?._map_ui?.search?.expression || null,
      queryText,
    });
  }

  observeProjectionChange(previous, next) {
    if (!this._enteredStageKey || this._completed || this._advancing) {
      return;
    }
    const previousSignals = tutorialProjectionState(previous);
    const signals = this.projectionState();
    if (!sameProgress(previous.ui?.search, next.ui?.search)) {
      this.observeQueryState(
        this._shell?.querySelector?.("#fishymap-search")?.value || "",
      );
    }
    if (!sameProgress(previous.bookmarks?.entries, next.bookmarks?.entries)) {
      this.observe({
        kind: "bookmark-count-changed",
        count: storedBookmarkCount(signals),
        bookmarkIds: storedBookmarkIds(signals),
      });
    }
    if (
      !sameProgress(
        previous.ui?.bookmarks?.selectedIds,
        next.ui?.bookmarks?.selectedIds,
      )
    ) {
      this.observe({
        kind: "bookmark-selection-changed",
        count: selectedBookmarkCount(signals),
      });
    }
    if (
      this._pendingLayerSettingsId &&
      !sameProgress(
        previous.ui?.layers?.expandedLayerIds,
        next.ui?.layers?.expandedLayerIds,
      )
    ) {
      const layerId = this._pendingLayerSettingsId;
      this._pendingLayerSettingsId = "";
      this.observe({
        kind: "layer-settings-changed",
        layerId,
        expanded: expandedLayerIds(signals).has(layerId),
      });
    }
    if (
      this._pendingLayerVisibilityId &&
      !sameProgress(
        previous.bridged?.filters?.layerIdsVisible,
        next.bridged?.filters?.layerIdsVisible,
      )
    ) {
      const layerId = this._pendingLayerVisibilityId;
      this._pendingLayerVisibilityId = "";
      this.observe({
        kind: "layer-visibility-changed",
        layerId,
        visible: visibleLayerIds(signals).has(layerId),
      });
    }
    if (
      this._pendingLayerHoverFact &&
      !sameProgress(
        previous.ui?.layers?.hoverFactsVisibleByLayer,
        next.ui?.layers?.hoverFactsVisibleByLayer,
      )
    ) {
      const { layerId, factKey } = this._pendingLayerHoverFact;
      this._pendingLayerHoverFact = null;
      this.observe({
        kind: "layer-hover-fact-changed",
        layerId,
        factKey,
        visible: hoverFactVisible(signals, layerId, factKey),
      });
    }
    const previousOpacity = layerOpacity(previousSignals, "fish_evidence");
    const nextOpacity = layerOpacity(signals, "fish_evidence");
    if (!Object.is(previousOpacity, nextOpacity)) {
      this.observe({
        kind: "layer-opacity-changed",
        layerId: "fish_evidence",
        opacity: nextOpacity,
      });
    }
    const previousClipMask = layerClipMask(previousSignals, "regions");
    const nextClipMask = layerClipMask(signals, "regions");
    if (previousClipMask !== nextClipMask) {
      this.observe({
        kind: "layer-clip-changed",
        layerId: "regions",
        maskLayerId: nextClipMask,
      });
    }
    const previousSearchWindow = windowState(previousSignals, "search");
    const nextSearchWindow = windowState(signals, "search");
    if (!sameProgress(previousSearchWindow, nextSearchWindow)) {
      this.observe({
        kind: "window-state-changed",
        ...nextSearchWindow,
      });
    }
    const previousLayersWindow = windowState(previousSignals, "layers");
    const nextLayersWindow = windowState(signals, "layers");
    if (!sameProgress(previousLayersWindow, nextLayersWindow)) {
      this.observe({
        kind: "window-state-changed",
        ...nextLayersWindow,
      });
    }
    this.schedulePresentation();
  }

  observe(observation) {
    if (!this._enteredStageKey || this._completed || this._advancing) {
      return;
    }
    const result = reduceTutorialInteraction(
      this.activeAction(),
      this._progress,
      observation,
    );
    const progressChanged = !sameProgress(this._progress, result.progress);
    this._progress = result.progress;
    if (
      this.activeAction() === "place-bookmark" &&
      result.progress.createdBookmarkId
    ) {
      this._lessonContext.createdBookmarkId =
        result.progress.createdBookmarkId;
    }
    if (result.complete) {
      return this.completeStep();
    }
    if (progressChanged) {
      this.queueRender();
      this.schedulePresentation();
    }
  }

  completeStep() {
    const lessonId = this.getAttribute?.("data-lesson") || "";
    const tutorial = MAP_TUTORIALS[String(lessonId).trim()];
    const stepIndex = normalizedTutorialStep(
      this.getAttribute?.("data-step"),
      tutorial?.steps?.length || 0,
    );
    if (!tutorial) {
      return;
    }
    if (stepIndex >= tutorial.steps.length - 1) {
      if (this._completionOperation) {
        return this._completionOperation.acknowledgement;
      }
      const progressId = mapTutorialProgressId(lessonId);
      const operation = {
        acknowledgement: null,
        lessonId: String(lessonId).trim(),
        progressId,
        stepIndex,
      };
      this._completionOperation = operation;
      this._advancing = true;
      try {
        const acknowledgement = this._tutorialProgressRepository.complete(
          [progressId],
          tutorialProgressCommandOptions(this._tutorialProgressState),
        );
        operation.acknowledgement = acknowledgement.then(
          (state) => {
            this.settleCompletionOperation(operation, state);
            return state;
          },
          () => {
            this.failCompletionOperation(
              operation,
              "repository-command-rejected",
            );
            return null;
          },
        );
      } catch {
        this.failCompletionOperation(operation, "repository-command-threw");
        return null;
      }
      return operation.acknowledgement;
    }
    this._advancing = true;
    this.clearSpotlight();
    this.restorePromotedSurface();
    this.setAttribute?.("data-step", String(stepIndex + 1));
    this.queueRender();
  }

  schedulePresentation() {
    if (this.hidden || this._completed) {
      return;
    }
    this._presentationInvalidated = true;
    if (this._frameId) {
      return;
    }
    if (typeof globalThis.requestAnimationFrame !== "function") {
      this._presentationInvalidated = false;
      this.present();
      return;
    }
    this._frameId = globalThis.requestAnimationFrame(
      this._followPresentationFrame,
    );
  }

  stopPresentationFollowing() {
    if (this._frameId && typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(this._frameId);
    }
    this._frameId = 0;
    this._presentationInvalidated = false;
    this._presentationGeometryKey = "";
    this._presentationTarget = null;
  }

  presentationFollowingActive() {
    return Boolean(
      this._shell &&
        !this.hidden &&
        !this._completed &&
        !this._paused &&
        this.activeAction(),
    );
  }

  activeCoachPresentation(target) {
    const lessonId = String(this.getAttribute?.("data-lesson") || "").trim();
    const tutorial = MAP_TUTORIALS[lessonId];
    const stepIndex = normalizedTutorialStep(
      this.getAttribute?.("data-step"),
      tutorial?.steps?.length || 0,
    );
    const step = tutorial?.steps?.[stepIndex] || null;
    const anchor = step?.coachAnchor === "query-surface"
      ? this._shell?.querySelector?.("#fishymap-query-surface") || target
      : target;
    return {
      anchor,
      placement: step?.coachPlacement || "auto",
    };
  }

  presentationGeometry() {
    const actionTarget = tutorialActionTarget(
      this.activeAction(),
      this._shell,
      this._progress,
      this._mapShellProjection,
    );
    if (!actionTarget) {
      return { key: "missing", target: null };
    }
    const target = tutorialTargetScrollState(actionTarget).presentationTarget;
    const targetDialog = target?.closest?.("dialog[open]") || null;
    const targetWindow = targetDialog
      ? null
      : target?.closest?.("[data-window-id]") || null;
    const targetSurface =
      targetDialog ||
      targetWindow ||
      target?.closest?.("#fishymap-toolbar, #fishymap-view-controls") ||
      null;
    const coachPresentation = this.activeCoachPresentation(target);
    const obstacles = tutorialObstacleBounds(this._shell, target, this);
    const key = [
      this.getAttribute?.("data-lesson") || "",
      this.getAttribute?.("data-step") || "",
      rectangleGeometryKey(this._shell?.getBoundingClientRect?.()),
      rectangleGeometryKey(target?.getBoundingClientRect?.()),
      rectangleGeometryKey(targetSurface?.getBoundingClientRect?.()),
      rectangleGeometryKey(
        coachPresentation.anchor?.getBoundingClientRect?.(),
      ),
      rectangleGeometryKey(this.getBoundingClientRect?.(), { sizeOnly: true }),
      ...obstacles.map((bounds) => rectangleGeometryKey(bounds)),
    ].join("|");
    return { key, target };
  }

  followPresentationFrame() {
    this._frameId = 0;
    if (!this.presentationFollowingActive()) {
      this._presentationInvalidated = false;
      this._presentationGeometryKey = "";
      this._presentationTarget = null;
      return;
    }
    const geometry = this.presentationGeometry();
    const geometryChanged =
      geometry.target !== this._presentationTarget ||
      geometry.key !== this._presentationGeometryKey;
    if (this._presentationInvalidated || geometryChanged) {
      this._presentationInvalidated = false;
      this._presentationGeometryKey = geometry.key;
      this._presentationTarget = geometry.target;
      this.present();
    }
    if (!this._frameId && typeof globalThis.requestAnimationFrame === "function") {
      this._frameId = globalThis.requestAnimationFrame(
        this._followPresentationFrame,
      );
    }
  }

  present() {
    this.clearSpotlight();
    const actionTarget = tutorialActionTarget(
      this.activeAction(),
      this._shell,
      this._progress,
      this._mapShellProjection,
    );
    if (!actionTarget) {
      return;
    }
    const scrollState = tutorialTargetScrollState(actionTarget);
    const currentScrollDirection = String(
      this._progress.scrollDirection || "",
    );
    if (currentScrollDirection !== scrollState.direction) {
      if (scrollState.direction) {
        this._progress = {
          ...this._progress,
          scrollDirection: scrollState.direction,
        };
      } else {
        const { scrollDirection: _scrollDirection, ...progress } = this._progress;
        this._progress = progress;
      }
      this.queueRender();
    }
    const target = scrollState.presentationTarget;
    const shellBounds = this._shell?.getBoundingClientRect?.();
    const targetBounds = target.getBoundingClientRect?.();
    const coachPresentation = this.activeCoachPresentation(target);
    const coachAnchorBounds =
      coachPresentation.anchor?.getBoundingClientRect?.() || targetBounds;
    const manager = this._shell?.querySelector?.("fishymap-window-manager");
    const targetDialog = target.closest?.("dialog[open]") || null;
    if (targetDialog) {
      this.hostFollowerInDialog(targetDialog);
    } else {
      this.restoreFollowerHome();
    }
    const coachBounds = this.getBoundingClientRect?.();
    const targetWindow = targetDialog
      ? null
      : target.closest?.("[data-window-id]") || null;
    const targetSurface =
      targetDialog ||
      targetWindow ||
      target.closest?.("#fishymap-toolbar, #fishymap-view-controls") ||
      null;
    let targetZIndex = manager?.highestWindowZIndex?.() ?? 20;
    if (targetWindow) {
      this.restorePromotedSurface();
      targetZIndex = manager?.bringToFront?.(
        targetWindow.getAttribute?.("data-window-id"),
      ) ?? targetZIndex;
    } else if (targetSurface) {
      if (this._promotedSurface !== targetSurface) {
        this.restorePromotedSurface();
        this._promotedSurface = targetSurface;
        this._promotedSurfaceZIndex = targetSurface.style?.zIndex || "";
      }
      targetZIndex = Math.max(targetZIndex + 1, targetDialog ? 2098 : 39);
      targetSurface.style.zIndex = String(targetZIndex);
    } else {
      this.restorePromotedSurface();
    }
    this.style.zIndex = String(Math.max(2100, targetZIndex + 1));
    if (shellBounds && coachBounds && targetBounds) {
      if (targetDialog) {
        this.hideVignette();
      } else {
        this.showVignette(shellBounds, targetBounds, targetZIndex);
      }
      const position = chooseTutorialCoachPosition(
        shellBounds,
        coachBounds,
        coachAnchorBounds,
        12,
        tutorialObstacleBounds(this._shell, target, this),
        coachPresentation.placement,
      );
      const viewportOffsetX = this._popoverOpen
        ? Number(shellBounds.left) || 0
        : 0;
      const viewportOffsetY = this._popoverOpen
        ? Number(shellBounds.top) || 0
        : 0;
      this.style.left = `${position.x + viewportOffsetX}px`;
      this.style.top = `${position.y + viewportOffsetY}px`;
    }
    this._spotlight = target;
    target.classList?.add?.(...SPOTLIGHT_CLASSES);
  }

  clearSpotlight() {
    this._spotlight?.classList?.remove?.(...SPOTLIGHT_CLASSES);
    this._spotlight = null;
    this.hideVignette();
  }

  ensureVignette() {
    if (this._vignette?.parentNode === this._shell) {
      return this._vignette;
    }
    this._vignette = null;
    const documentOwner = this.ownerDocument || globalThis.document;
    if (!this._shell || typeof documentOwner?.createElement !== "function") {
      return null;
    }
    const vignette = documentOwner.createElement("div");
    vignette.hidden = true;
    vignette.setAttribute("aria-hidden", "true");
    vignette.setAttribute("data-tutorial-vignette", "");
    vignette.className =
      "pointer-events-none absolute inset-0 hidden overflow-hidden not-prose";
    const hole = documentOwner.createElement("span");
    hole.setAttribute("data-tutorial-vignette-hole", "");
    vignette.appendChild(hole);
    this._shell.appendChild?.(vignette);
    this._vignette = vignette;
    return vignette;
  }

  showVignette(shellBounds, targetBounds, targetZIndex) {
    const vignette = this.ensureVignette();
    if (!vignette) {
      return;
    }
    const rectangle = tutorialVignetteHoleRect(shellBounds, targetBounds, 10);
    vignette.hidden = false;
    vignette.classList?.remove?.("hidden");
    vignette.style.zIndex = String(Math.max(40, Number(targetZIndex) + 1));
    const hole = vignette.querySelector?.("[data-tutorial-vignette-hole]");
    if (hole?.style) {
      hole.style.left = `${rectangle.left}px`;
      hole.style.top = `${rectangle.top}px`;
      hole.style.width = `${rectangle.width}px`;
      hole.style.height = `${rectangle.height}px`;
    }
  }

  hideVignette() {
    if (!this._vignette) {
      return;
    }
    this._vignette.hidden = true;
    this._vignette.classList?.add?.("hidden");
  }

  removeVignette() {
    this._vignette?.remove?.();
    this._vignette = null;
  }

  restorePromotedSurface() {
    if (!this._promotedSurface?.style) {
      this._promotedSurface = null;
      this._promotedSurfaceZIndex = "";
      return;
    }
    if (this._promotedSurfaceZIndex) {
      this._promotedSurface.style.zIndex = this._promotedSurfaceZIndex;
    } else {
      this._promotedSurface.style.removeProperty?.("z-index");
      this._promotedSurface.style.zIndex = "";
    }
    this._promotedSurface = null;
    this._promotedSurfaceZIndex = "";
  }

  reset() {
    this.invalidateCompletionOperation();
    this.stopPresentationFollowing();
    this.clearSpotlight();
    this.removeVignette();
    this.restorePromotedSurface();
    this.hideFollowerPopover();
    this.restoreFollowerHome({ show: false });
    this._enteredStageKey = "";
    this._renderedHistoryKey = "";
    this._progress = {};
    this._lessonContext = {};
    this._completed = false;
    this.clearCompletionFailure();
    this._advancing = false;
    this._paused = false;
    this._pendingLayerSettingsId = "";
    this._pendingLayerVisibilityId = "";
    this._pendingLayerHoverFact = null;
    this.exitFollowerMode();
    this.removeAttribute?.("data-lesson");
    this.removeAttribute?.("data-step");
    this.innerHTML = "";
    this.hidden = true;
    this.style.removeProperty?.("left");
    this.style.removeProperty?.("top");
    this.style.removeProperty?.("z-index");
    this.updateCatalogControls();
  }
}

if (
  globalThis.customElements &&
  !globalThis.customElements.get(TUTORIAL_STAGE_TAG_NAME)
) {
  globalThis.customElements.define(
    TUTORIAL_STAGE_TAG_NAME,
    FishyMapTutorialStageElement,
  );
}
